import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TeammateAccountGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  computeForwardDateRangeBounds,
  formatForwardDateRangeCondition,
  type ForwardDateRange,
  type ForwardDateRangeBounds,
} from "@repo/ui/dateRange/forwardDateRange";
import { ForwardDateRangeDialog } from "@repo/ui/dateRange/forwardDateRangeDialog";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { popoverStyle } from "@repo/ui/popover/style";
import { selectStyle } from "@repo/ui/Select/style";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconSearch, IconSelector } from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { Button, DialogTrigger, Popover } from "react-aria-components";
import { useSearchParams } from "react-router";
import {
  usePlaybookTeammateUpcomingGradingsCountQuery,
  usePlaybookTeammateUpcomingGradingsInfiniteQuery,
} from "../../api/entities/performancePlaybook.js";
import { PageBody, TableCard } from "../../components/layout/pageLayout.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import { FilterResultSummary } from "../filters/filterResultSummary.js";
import { buildGradingModalPath } from "../teammatePerformance/gradingHelpers.js";
import { GradingsTable } from "../teammatePerformance/gradingsTable.js";

const SEARCH_DEBOUNCE_MS = 500;

/** The request schema's limit on a customer search. */
const MAX_SEARCH_CHARS = 100;

const chipStyle = selectStyle({ size: Size.SMALL });

interface NextActionRange {
  value: ForwardDateRange;
  /**
   * Resolved when the range is picked rather than on every render, so the
   * request keeps its args (and its pages) while time moves on.
   */
  bounds: ForwardDateRangeBounds;
}

const ANY_TIME: NextActionRange = { value: { type: "any" }, bounds: {} };

/** Polls so calls the sweeper admits leave the list while the page is open. */
const POLLING_INTERVAL_MS = 60_000;

/** Upcoming's search and range, held above it so they survive a view toggle. */
export interface UpcomingCallsFilters {
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  /** The trimmed, debounced search, as the requests take it. */
  search: string | undefined;
  range: NextActionRange;
  setRange: (range: NextActionRange) => void;
  reset: () => void;
}

export function useUpcomingCallsFilters(): UpcomingCallsFilters {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
    };
  }, [searchQuery]);
  const search = debouncedSearch.trim() || undefined;

  const [range, setRange] = useState<NextActionRange>(ANY_TIME);

  const reset = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setRange(ANY_TIME);
  };

  return { searchQuery, setSearchQuery, search, range, setRange, reset };
}

export interface TeammateAgentUpcomingCallsProps {
  playbookId: PlaybookId;
  teammateAgentId: TeammateId;
  outcomeDefinition: OutcomeDefinition | undefined;
  selectedCustomFieldDefinitions: readonly CustomFieldDefinition[];
  callActivityColumns:
    | readonly EntityTableColumnDefinition<TeammateAccountGrading>[]
    | undefined;
  filters: UpcomingCallsFilters;
  rightActions: ReactNode;
}

/**
 * The voice agent's interactions with a call still to run, soonest first. Its
 * search and range are its own rather than the shared filters', which describe
 * calls that have happened.
 */
export const TeammateAgentUpcomingCalls: FC<TeammateAgentUpcomingCallsProps> = (
  props,
) => {
  const { playbookId, teammateAgentId } = props;
  const { searchQuery, setSearchQuery, search, range, setRange } =
    props.filters;

  const modalNavigation = useModalNavigation();
  const [searchParams] = useSearchParams();
  const now = useNow();

  const args = {
    playbookId,
    teammateId: teammateAgentId,
    scheduledAfter: range.bounds.scheduledAfter,
    scheduledBefore: range.bounds.scheduledBefore,
    search,
  };

  const pollingOptions = {
    pollingInterval: POLLING_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  };

  // `currentData`, not `data`, as on Recent: the latter falls back to the
  // previous args' result when the current request fails, which would leave
  // the old rows under the new count.
  const { currentData: gradingCount, isFetching: isFetchingGradingCount } =
    usePlaybookTeammateUpcomingGradingsCountQuery(args, pollingOptions);

  const {
    currentData: gradingsData,
    isFetching: isFetchingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
  } = usePlaybookTeammateUpcomingGradingsInfiniteQuery(args, pollingOptions);

  const gradings = useMemo(
    () => gradingsData?.pages.flatMap((page) => page.upcomingGradings) ?? [],
    [gradingsData],
  );

  // Only while there is nothing for the current args yet, so neither a poll
  // nor appending a page blanks the table.
  const isLoadingGradings =
    (isFetchingGradingCount && gradingCount === undefined) ||
    (isFetchingGradings && gradingsData === undefined);

  const isNarrowed = search !== undefined || range.value.type !== "any";

  return (
    <>
      <PageHeaderFilterBarPortal>
        <FilterBar
          isLoading={isLoadingGradings}
          leadingItems={
            <div className="w-64 shrink-0">
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery("")}
                leftIcon={<IconSearch className="h-4 w-4 text-gray-400" />}
                maxLength={MAX_SEARCH_CHARS}
                size={Size.SMALL}
              />
            </div>
          }
          items={
            <NextActionRangeFilterItem
              value={range.value}
              onChange={(value) => {
                setRange({
                  value,
                  bounds: computeForwardDateRangeBounds(value, now),
                });
              }}
            />
          }
          resultSummary={
            gradingCount !== undefined ? (
              <FilterResultSummary count={gradingCount} />
            ) : undefined
          }
          onReset={isNarrowed ? props.filters.reset : undefined}
          rightActions={props.rightActions}
        />
      </PageHeaderFilterBarPortal>
      <PageBody>
        <TableCard>
          <GradingsTable
            outcomeDefinition={props.outcomeDefinition}
            gradings={gradings}
            isLoading={isLoadingGradings}
            isError={isErrorGradings}
            onLoadMore={canFetchMoreGradings ? fetchMoreGradings : undefined}
            onGradingAction={(grading) => {
              // Carries the view (and any `?q=` Recent was opened with) along,
              // so a reload with the slideover open keeps Upcoming underneath.
              const nextSearchParams = new URLSearchParams(searchParams);
              nextSearchParams.set("view", "upcoming");
              modalNavigation.push({
                pathname: buildGradingModalPath(grading.id),
                search: nextSearchParams.toString(),
              });
            }}
            selectedCustomFieldDefinitions={
              props.selectedCustomFieldDefinitions
            }
            additionalColumns={props.callActivityColumns}
            showsPlaybookColumn={false}
            emptyMessage={
              isNarrowed
                ? "No upcoming calls match your filters."
                : "No upcoming calls."
            }
            isFullBleed
          />
        </TableCard>
      </PageBody>
    </>
  );
};

interface NextActionRangeFilterItemProps {
  value: ForwardDateRange;
  onChange: (newValue: ForwardDateRange) => void;
}

/**
 * The range chip, behind a fixed "Next action" segment so it says which date
 * it applies to — the same joined look as the Recent date filter.
 */
const NextActionRangeFilterItem: FC<NextActionRangeFilterItemProps> = (
  props,
) => {
  return (
    // `isolate` so the focus ring's z-10 competes only with the fixed segment.
    <div className="isolate flex min-w-0 items-center">
      <div
        className={chipStyle.button({
          className:
            "rounded-r-none bg-gray-100 text-muted-foreground hover:bg-gray-100 active:bg-gray-100",
        })}
      >
        <span className={chipStyle.value({ className: "max-w-44" })}>
          Next action
        </span>
      </div>
      <DialogTrigger>
        <Button
          className={chipStyle.button({
            className:
              "-ml-px rounded-l-none focus-visible:z-10 data-[focus-visible]:z-10",
          })}
        >
          <span className={chipStyle.value()}>
            {formatForwardDateRangeCondition(props.value)}
          </span>
          <IconSelector className={chipStyle.chevron()} />
        </Button>

        <Popover
          placement="bottom start"
          className={popoverStyle().popover({ className: "min-w-0" })}
        >
          <ForwardDateRangeDialog
            value={props.value}
            onChange={props.onChange}
          />
        </Popover>
      </DialogTrigger>
    </div>
  );
};
